<script setup>
import { computed } from 'vue';
import { useField } from 'vee-validate';
import ColorPicker from 'primevue/colorpicker';
import { corValida, normalizarCor } from './CorCampo.validacao';

const props = defineProps({
    id: {
        type: String,
        default: undefined
    },
    // Classes da coluna em volta do campo.
    classeColuna: {
        type: String,
        default: 'col-12'
    },
    // Nome do campo no schema do useForm. Quando informado, o componente se conecta
    // sozinho ao formulário pai (valor e erro), dispensando v-model e :erro.
    name: {
        type: String,
        default: null
    },
    erro: {
        type: String,
        default: null
    },
    modelValue: {
        type: String,
        default: null
    },
    titulo: {
        type: String,
        default: 'Cor'
    },
    help: {
        type: String,
        default: null
    }
});

const emit = defineEmits(['update:modelValue']);

const PALETA = [
    { nome: 'Azul', cor: '#1A73E8' },
    { nome: 'Ciano', cor: '#00ACC1' },
    { nome: 'Verde', cor: '#34A853' },
    { nome: 'Amarelo', cor: '#F9AB00' },
    { nome: 'Laranja', cor: '#F4511E' },
    { nome: 'Vermelho', cor: '#D93025' },
    { nome: 'Rosa', cor: '#E91E63' },
    { nome: 'Roxo', cor: '#8E24AA' },
];

const campoForm = props.name ? useField(() => props.name) : null;

const cor = computed({
    get: () => (campoForm ? campoForm.value.value : props.modelValue) ?? '',
    set: (valor) => {
        const normalizada = normalizarCor(valor);

        if (campoForm) campoForm.value.value = normalizada;
        emit('update:modelValue', normalizada);
    }
});

// Enquanto o texto está incompleto, o seletor volta para o branco de "sem cor".
const corSeletor = computed({
    get: () => (corValida(cor.value) ? cor.value.slice(1) : null),
    set: (valor) => {
        cor.value = valor;
    }
});

const mensagemErro = computed(() => props.erro ?? campoForm?.errorMessage.value ?? null);
</script>

<template>
    <div class="field mb-2" :class="props.classeColuna">
        <SmallTitle :titulo="props.titulo" :help="props.help" />
        <div class="flex align-items-center justify-content-between py-1 mb-2" style="max-width: 20rem;">
            <button type="button" v-tooltip.top="'Sem cor'" aria-label="Sem cor" :aria-pressed="!cor"
                class="flex-shrink-0 p-0 border-circle cursor-pointer surface-card border-1 surface-border flex align-items-center justify-content-center text-500"
                :style="{ width: '1.5rem', height: '1.5rem', ...(!cor ? { outline: '2px solid var(--text-color-secondary)', outlineOffset: '2px' } : {}) }"
                @click="cor = ''">
                <i class="pi pi-ban text-xs" />
            </button>
            <button v-for="item in PALETA" :key="item.cor" type="button" v-tooltip.top="item.nome" :aria-label="item.nome"
                :aria-pressed="cor === item.cor" class="flex-shrink-0 p-0 border-circle cursor-pointer border-none"
                :style="{ width: '1.5rem', height: '1.5rem', background: item.cor, ...(cor === item.cor ? { outline: `2px solid ${item.cor}`, outlineOffset: '2px' } : {}) }"
                @click="cor = item.cor" />
        </div>
        <div class="relative">
            <ColorPicker v-model="corSeletor" defaultColor="ffffff" class="absolute top-50 left-0 ml-2 z-1"
                style="transform: translateY(-50%); line-height: 0;"
                :pt="{ input: { class: 'border-round-sm', style: 'width: 1.5rem; height: 1.5rem;', 'aria-label': 'Escolher cor personalizada' } }" />
            <InputText :id="props.id" v-model="cor" class="text-sm w-full" style="padding-left: 2.75rem;"
                placeholder="Personalizada (#RRGGBB)" maxlength="7" :class="{ 'p-invalid': mensagemErro }" />
        </div>
        <SmallError v-if="mensagemErro" :error="mensagemErro" />
    </div>
</template>
